/*
 * <custom-product-row>
 * Lazily loads a single row of products and injects them, reusing the minimal
 * product card. Two modes:
 *   data-mode="recommendations" — fetches the Product Recommendations API
 *   data-mode="recently"        — fetches recently-viewed via search-by-id
 * No libraries. Degrades to nothing if there's no data.
 */
(function () {
  'use strict';

  const VIEWED_KEY = 'viewedProducts';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function getViewed() {
    try {
      return JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  class CustomProductRow extends HTMLElement {
    connectedCallback() {
      this.mode = this.getAttribute('data-mode');
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries, obs) => {
            if (entries[0] && entries[0].isIntersecting) {
              obs.disconnect();
              this.load();
            }
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this._io.observe(this);
      } else {
        this.load();
      }
    }

    load() {
      if (this.mode === 'recommendations') this.loadRecommendations();
      else if (this.mode === 'recently') this.loadRecently();
    }

    /* ----------------------------------------------------- Related products */
    loadRecommendations() {
      // Already rendered server-side (recommendations.performed) — nothing to do.
      if (this.getAttribute('data-performed') === 'true') return;
      const url = this.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('custom-product-row');
          if (fresh) this.innerHTML = fresh.innerHTML;
          else this.replaceChildren();
        })
        .catch(() => this.replaceChildren());
    }

    /* ----------------------------------------------------- Recently viewed */
    loadRecently() {
      const current = this.getAttribute('data-current-id');
      const limit = parseInt(this.getAttribute('data-limit') || '4', 10);
      const sectionId = this.getAttribute('data-section-id');

      let ids = getViewed()
        .map((id) => String(id))
        .filter((id) => id && id !== String(current));
      ids = ids.slice(0, limit);

      if (!ids.length || !sectionId) {
        this.replaceChildren();
        return;
      }

      const q = ids.map((id) => 'id:' + id).join(' OR ');
      const url =
        root +
        'search?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product&section_id=' +
        encodeURIComponent(sectionId);

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('custom-product-row');
          if (fresh) {
            this.innerHTML = fresh.innerHTML;
            this.reorder(ids);
          } else {
            this.replaceChildren();
          }
        })
        .catch(() => this.replaceChildren());
    }

    // Search returns by relevance; restore the recency order.
    reorder(ids) {
      const list = this.querySelector('.cpr__grid');
      if (!list) return;
      const items = Array.from(list.children);
      const rank = (el) => {
        const i = ids.indexOf(String(el.getAttribute('data-product-id')));
        return i < 0 ? 999 : i;
      };
      items.sort((a, b) => rank(a) - rank(b));
      items.forEach((el) => list.appendChild(el));
    }
  }

  if (!customElements.get('custom-product-row')) {
    customElements.define('custom-product-row', CustomProductRow);
  }
})();
